// src/features/display/useDisplayTriggers.js
import { useEffect, useRef } from 'react';
import { soundConfig } from '../../constants/display/soundConfig';

export const useDisplayTriggers = ({
  structure,
  currentLevel,
  remainingTime,
  timerState,
  lastTimerUpdateTriggerType,
  settings,
  playSound,
  speak,
  isIntegratedViewEnabled,
  appConfig,
  firstActualLevelIndex
}) => {
  const warningFiredForLevelRef = useRef({});
  const prevTimerStateRef = useRef(timerState);
  const prevLevelRef = useRef(currentLevel);

  // Handle Level End Warning
  useEffect(() => {
    const currentLevelData = structure?.levels?.[currentLevel];
    if (!isIntegratedViewEnabled) return;

    if (currentLevelData && currentLevel !== warningFiredForLevelRef.current.level) {
      warningFiredForLevelRef.current = { level: currentLevel, fired: false };
    }

    if (
      timerState === 'play' &&
      (!lastTimerUpdateTriggerType || lastTimerUpdateTriggerType === 'tick') &&
      currentLevelData?.type !== 'break' &&
      remainingTime === settings.levelEndWarning.secondsBeforeEnd &&
      !warningFiredForLevelRef.current.fired
    ) {
      warningFiredForLevelRef.current.fired = true;
      const { enableSound, selectedSound, maxDuration, enableSpeech, speechText } = settings.levelEndWarning;

      const onSoundEnd = (enableSpeech && speechText) ? () => speak(speechText, currentLevelData, structure.columns, false) : null;

      if (enableSound && selectedSound) {
        playSound(soundConfig.levelEndWarning.soundId, maxDuration, onSoundEnd, false);
      } else if (enableSpeech && speechText) {
        speak(speechText, currentLevelData, structure.columns, false);
      }
    }
  }, [remainingTime, timerState, lastTimerUpdateTriggerType, currentLevel, structure, settings.levelEndWarning, playSound, speak, isIntegratedViewEnabled, appConfig]);

  // Handle Sound Triggers (Manual Start, Level Advance)
  useEffect(() => {
    const prevState = prevTimerStateRef.current;
    const currState = timerState;
    const prevLevel = prevLevelRef.current;
    const currLevel = currentLevel;

    prevTimerStateRef.current = currState;
    prevLevelRef.current = currLevel;

    if (!isIntegratedViewEnabled || !structure) return;

    let shouldTrigger = false;
    let isStartTrigger = false;

    if (prevState === 'ready' && currState === 'play') {
      const currentLevelData = structure?.levels?.[currLevel];
      if (currentLevelData && currentLevelData.type !== 'break') {
        shouldTrigger = true;
        isStartTrigger = true;
      }
    }

    if (!isStartTrigger && currState === 'play' && prevLevel !== currLevel) {
      // Only trigger if we had a valid previous state (avoids triggering on initial load)
      if (prevState !== undefined && prevState !== null) {
        shouldTrigger = true;
      }
    }

    if (shouldTrigger) {
      const currentLevelData = structure?.levels?.[currLevel];
      if (!currentLevelData) return;

      let actionsForTrigger = null;
      let soundId = null;

      if (currentLevelData.type === 'level') {
        if (currLevel === firstActualLevelIndex) {
          actionsForTrigger = settings.tournamentStart;
          soundId = soundConfig.tournamentStart.soundId;
        } else {
          actionsForTrigger = settings.levelStart;
          soundId = soundConfig.levelStart.soundId;
        }
      } else if (currentLevelData.type === 'break') {
        actionsForTrigger = settings.breakStart;
        soundId = soundConfig.breakStart.soundId;
      }

      if (actionsForTrigger && actionsForTrigger.enableSound && actionsForTrigger.selectedSound) {
        const onSoundEnd = (actionsForTrigger.enableSpeech && actionsForTrigger.speechText)
          ? () => speak(actionsForTrigger.speechText, currentLevelData, structure.columns, false)
          : null;

        playSound(soundId, actionsForTrigger.maxDuration, onSoundEnd, false);
      } else if (actionsForTrigger && actionsForTrigger.enableSpeech && actionsForTrigger.speechText) {
        speak(actionsForTrigger.speechText, currentLevelData, structure.columns, false);
      }
    }
  }, [timerState, currentLevel, firstActualLevelIndex, isIntegratedViewEnabled, structure, settings, playSound, speak, appConfig]);
};
